import { GRAYS, PALETTE } from "@eldrun/content";
import { Color, DoubleSide, ShaderMaterial, SRGBColorSpace, Vector2 } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";

const vertexShader = /* glsl */ `
out vec2 vUv;
out vec3 vWorldPos;
out vec3 vNormalW;

void main() {
  vUv = uv;
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorldPos = world.xyz;
  vNormalW = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "noise"])}
uniform vec2 uCentro;
uniform float uRaio;
uniform float uRaioProximo;
uniform float uDistJogador;
uniform float uTempo;
uniform vec3 uFogoBase;
uniform vec3 uFogoPonta;
in vec2 vUv;
in vec3 vWorldPos;
in vec3 vNormalW;

void main() {
  // Chamas rolantes subindo pela parede; mais altas e opacas com o jogador perto da borda.
  float near = 1.0 - smoothstep(0.0, 60.0, abs(uDistJogador));
  float around = atan(vWorldPos.z - uCentro.y, vWorldPos.x - uCentro.x) * uRaio * 0.08;
  float h = vUv.y;
  vec2 p = vec2(around, h * 6.0 - uTempo * 1.6);
  float n = noise(p) * 0.6 + noise(p * 2.3 + 3.0) * 0.4;
  // edge0 < edge1 sempre (GLSL ES 3.0: smoothstep com limites invertidos é indefinido).
  float flame = smoothstep(min(0.25 + h * (0.9 - near * 0.4), 0.84), 0.85, n + (1.0 - h) * 0.6);
  vec3 viewDir = normalize(cameraPosition - vWorldPos);
  float fresnel = pow(1.0 - abs(dot(viewDir, vNormalW)), 2.0);
  vec3 color = mix(uFogoBase, uFogoPonta, flame) * (1.2 + fresnel);
  // Malha neon (F60): perigo neutro (não é de um chefe), em branco pela luminância.
  color = vec3(dot(color, vec3(0.2126, 0.7152, 0.0722))) * 1.3;
  float alpha = (0.15 + 0.6 * flame + 0.25 * fresnel) * (0.35 + 0.65 * near) * (1.0 - smoothstep(0.7, 1.0, h));
  gl_FragColor = vec4(color, alpha);
  #include <colorspace_fragment>
}
`;

/** Cerco do Boitatá (doc 05 §10): parede cilíndrica de fogo, num único draw. */
export function createCercoMaterial(atmosphere: AtmosphereUniforms): ShaderMaterial {
  return new ShaderMaterial({
    name: "cerco",
    uniforms: {
      ...atmosphere,
      uCentro: { value: new Vector2() },
      uRaio: { value: 100 },
      uRaioProximo: { value: 100 },
      uDistJogador: { value: 100 },
      uTempo: { value: 0 },
      // Perigo neutro (doc 09 §1.3, F75): branco com o padrão próprio do fogo, sem o vermelho do chefe.
      uFogoBase: { value: new Color().setRGB(GRAYS.escuro, GRAYS.escuro, GRAYS.escuro, SRGBColorSpace) },
      uFogoPonta: { value: new Color(PALETTE.neutro.hex) },
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
  });
}
